import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
} from "@mui/material";
import { forwardRef, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import FormInputText from "../components/Form/FormInputText";
import FormSwitch from "../components/Form/FormSwitch";
import { generatePassword } from "../lib/helper";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { CompanyUser, CompanyUserType } from "../models/company.user.model";
import { emailPattern } from "../lib/format";
import FormSelection from "../components/Form/FormSelection";
import FormHierarchySelection from "../components/Form/FormHierarchySelection";

export default forwardRef(
  (
    props: {
      defaultValues?: CompanyUser;
    },
    ref: any,
  ) => {
    const methods = useForm<CompanyUser>({
      defaultValues: props.defaultValues || {
        enabled: true,
        emailVerified: true,
        password: generatePassword(),
      },
    });
    const { handleSubmit, control, setValue, setError, watch } = methods;
    const userType = watch("type");

    useImperativeHandle(ref, () => ({
      onSubmit() {
        return new Promise((resolve, reject) => {
          handleSubmit((v) => {
            resolve(v);
          }, reject)();
        });
      },
    }));

    return (
      <Box
        component="form"
        sx={{
          "& .MuiTextField-root": { mt: 1, mb: 1 },
        }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper className="padding-big">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bolder" }}>
                    Email Verified
                  </Typography>
                  <div className="all-center-wrapper between-wrapper">
                    <Typography variant="body2" color="text.secondary">
                      Disabling this will automatically send the user a verification email
                    </Typography>
                    <FormSwitch name="emailVerified" control={control} setError={setError} />
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bolder" }}>
                    Enabled
                  </Typography>
                  <div className="all-center-wrapper between-wrapper">
                    <Typography variant="body2" color="text.secondary">
                      Disabling this will not let the user login in anymore
                    </Typography>
                    <FormSwitch name="enabled" control={control} setError={setError} />
                  </div>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper className="padding-big">
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormInputText
                    disabled={!!(props.defaultValues && props.defaultValues.username)}
                    name="usernameOrEmail"
                    control={control}
                    setError={setError}
                    label="Email"
                    required
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: emailPattern,
                        message: "Please fill in valid email",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormInputText
                    name="name"
                    control={control}
                    setError={setError}
                    label="Full Name"
                    required
                    rules={{
                      required: "Full Name is required",
                      maxLength: {
                        value: 30,
                        message: "Full Name characters must be less than 30",
                      },
                      minLength: {
                        value: 3,
                        message: "Full Name characters must be more than 3",
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormSelection
                    options={[
                      { label: "Admin", value: "admin" },
                      { label: "Viewer", value: "viewer" },
                    ]}
                    name="type"
                    control={control}
                    setError={setError}
                    label="User Type"
                    required
                    rules={{
                      required: "User Type is required",
                    }}
                  />
                </Grid>

                {userType === CompanyUserType.viewer && (
                  <Grid item xs={12}>
                    <FormHierarchySelection
                      required
                      rules={{ required: "Hierarchy is required" }}
                      name="hierarchies"
                      multiple
                      control={control}
                      setError={setError}
                      label="Allowed Hierarchy Filter"
                      displayType="all"
                    />
                  </Grid>
                )}

                {!props.defaultValues && (
                  <Grid item xs={12}>
                    <FormInputText
                      name="password"
                      control={control}
                      setError={setError}
                      label="Password"
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="regenerate password"
                              edge="end"
                              onClick={() => {
                                setValue("password", generatePassword());
                              }}
                            >
                              <AutorenewIcon />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      rules={{
                        required: "Password is required",
                        maxLength: {
                          value: 30,
                          message: "Password characters must be less than 30",
                        },
                        minLength: {
                          value: 6,
                          message: "Password characters must be more than 6",
                        },
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  },
);
